import { parseDateOnlyInputValue, toDateOnlyInputValue } from "@/utils/date-only";
import TimeAgo from "javascript-time-ago";
import en from "javascript-time-ago/locale/en";
import onetime from "onetime";

export const addDefaultLocale = onetime(() => {
  TimeAgo.addDefaultLocale(en);
});

export type SerializedDateValue<T> = T extends Date ? string
  : T extends Array<infer Item> ? Array<SerializedDateValue<Item>>
  : T extends object ? { [Key in keyof T]: SerializedDateValue<T[Key]> }
  : T;

export function serializeDates<T>(value: T): SerializedDateValue<T> {
  if (value instanceof Date) {
    return value.toISOString() as SerializedDateValue<T>;
  }

  if (Array.isArray(value)) {
    return value.map(item => serializeDates(item)) as SerializedDateValue<T>;
  }

  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, serializeDates(item)]),
    ) as SerializedDateValue<T>;
  }

  return value as SerializedDateValue<T>;
}

export function parseDateValue(value: Date | string | null | undefined) {
  if (!value) {
    return null;
  }

  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Format a date to a human-readable string
 */
export function formatDate(value: Date | string | null | undefined): string {
  const date = parseDateValue(value);

  if (!date) {
    return "-";
  }

  return date.toLocaleDateString("en-US", {
    timeZone: "America/Los_Angeles",
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function toDateInputValue(value: Date | string | null | undefined) {
  return toDateOnlyInputValue(value);
}

export function fromDateInputValue(value: Date | string | null | undefined) {
  if (value instanceof Date) {
    return value;
  }

  return value ? parseDateOnlyInputValue(value) ?? new Date(Number.NaN) : undefined;
}

addDefaultLocale();
export const timeAgo = new TimeAgo("en-US");
